import React from "react";
import { useOutletContext } from "react-router-dom";
import {
  ANGER_TYPE_LABELS,
  ANGER_TYPE_TAGLINE,
  ANGER_TYPE_DESCRIPTION,
  ANGER_TYPE_COLORS,
  ANGER_TYPE_HEALING,
  ANGER_TYPE_SURVIVAL
} from "../../constants/angerPatterns.js";
import { HUB_ILLUSTRATIONS, HUB_CARD_ICONS } from "../../constants/mediaAssets.js";
import HubIllustration from "../HubIllustration.js";
import MediaIcon from "../MediaIcon.js";
import MentalPatternsBackLink from "./MentalPatternsBackLink.js";
import LockedPatternTease from "./LockedPatternTease.js";
import { useMentalPatterns } from "./MentalPatternsContext.js";
import { MIN_INSIGHTS_FOR_PATTERNS } from "../../constants/mentalPatternsHub.js";

const MentalPatternsAnger = () => {
  const { isSubscriber, verifiedEmail } = useOutletContext() || {};
  const { angerAnalysis, totalReflections, lockedPatternTease } = useMentalPatterns();
  const {
    angerInsights,
    totalAnger,
    healingCount,
    survivalCount,
    healthyAngerScore,
    healthyAngerLabel
  } = angerAnalysis;

  const angerPieTotal = totalAnger || 1;
  const showLockedTease =
    !isSubscriber && totalReflections >= MIN_INSIGHTS_FOR_PATTERNS && totalAnger > 0;

  if (totalAnger === 0) {
    return (
      <>
        <MentalPatternsBackLink />
        <div className="mental-patterns__hero">
          <HubIllustration src={HUB_ILLUSTRATIONS.anger} alt="" className="hub-illustration hub-illustration--anger" />
          <h1>Anger scale</h1>
        </div>
        <p>
          No anger-related reflections found yet. Patterns appear when reflections show anger-related emotions or
          language.
        </p>
      </>
    );
  }

  if (showLockedTease) {
    return (
      <>
        <MentalPatternsBackLink />
        <div className="mental-patterns__hero">
          <HubIllustration src={HUB_ILLUSTRATIONS.anger} alt="" className="hub-illustration hub-illustration--anger" />
          <h1>Anger scale</h1>
        </div>
        <LockedPatternTease tease={lockedPatternTease} verifiedEmail={verifiedEmail} />
      </>
    );
  }

  return (
    <>
      <MentalPatternsBackLink />
      <div className="mental-patterns__hero">
        <HubIllustration src={HUB_ILLUSTRATIONS.anger} alt="" className="hub-illustration hub-illustration--anger" />
        <h1>Anger scale</h1>
      </div>

      <section className="anger-patterns" aria-label="Anger pattern scale">
        <h2 className="anger-patterns__heading">Anger pattern scale</h2>
        <p className="anger-patterns__intro">
          Reflections showing anger, categorized as deliberate (recognition) or reactive (control).
        </p>

        <div className="anger-scale">
          <div className="anger-scale__labels">
            <span>Reactive</span>
            <span>Deliberate</span>
          </div>
          <div
            className="anger-scale__track"
            role="meter"
            aria-valuenow={healthyAngerScore}
            aria-valuemin={0}
            aria-valuemax={100}
            aria-label={`Anger pattern score: ${healthyAngerScore} out of 100, ${healthyAngerLabel}`}
          >
            <div className="anger-scale__gradient" />
            <div
              className="anger-scale__marker"
              style={{ left: `${healthyAngerScore}%` }}
              title={`${healthyAngerScore}/100`}
            />
          </div>
          <p className="anger-scale__score">
            <strong>{healthyAngerScore}</strong>
            <span className="anger-scale__score-max"> / 100</span>
            <span className="anger-scale__score-label"> — {healthyAngerLabel}</span>
          </p>
        </div>

        <div className="anger-patterns__split">
          <div className="anger-patterns__type-card anger-patterns__type-card--healing">
            <MediaIcon iconKey="heart" className="media-icon anger-patterns__type-icon" size={32} alt="" />
            <h3>{ANGER_TYPE_LABELS.healing}</h3>
            <p className="anger-patterns__type-tagline">{ANGER_TYPE_TAGLINE.healing}</p>
            <p className="anger-patterns__type-desc">{ANGER_TYPE_DESCRIPTION.healing}</p>
            <p className="anger-patterns__type-count">
              {healingCount} reflection{healingCount !== 1 ? "s" : ""}
            </p>
          </div>
          <div className="anger-patterns__type-card anger-patterns__type-card--survival">
            <MediaIcon iconKey={HUB_CARD_ICONS.anger} className="media-icon anger-patterns__type-icon" size={32} alt="" />
            <h3>{ANGER_TYPE_LABELS.survival}</h3>
            <p className="anger-patterns__type-tagline">{ANGER_TYPE_TAGLINE.survival}</p>
            <p className="anger-patterns__type-desc">{ANGER_TYPE_DESCRIPTION.survival}</p>
            <p className="anger-patterns__type-count">
              {survivalCount} reflection{survivalCount !== 1 ? "s" : ""}
            </p>
          </div>
        </div>
      </section>

      <section className="mood-distribution anger-patterns__distribution" aria-label="Anger type distribution">
        <h2>Anger distribution</h2>
        <div className="mood-distribution__pie-wrap">
          <div
            className="mood-pie mood-pie--anger"
            role="img"
            aria-label={`Deliberate ${healingCount}, Reactive ${survivalCount}`}
            style={{
              background: (() => {
                const healingPct = (healingCount / angerPieTotal) * 100;
                return `conic-gradient(${ANGER_TYPE_COLORS.healing} 0% ${healingPct}%, ${ANGER_TYPE_COLORS.survival} ${healingPct}% 100%)`;
              })()
            }}
          />
        </div>
        <div className="mood-pie-legend">
          {[ANGER_TYPE_HEALING, ANGER_TYPE_SURVIVAL].map((key) => {
            const count = key === ANGER_TYPE_HEALING ? healingCount : survivalCount;
            const pct = totalAnger ? (count / totalAnger) * 100 : 0;
            return (
              <div key={key} className="mood-pie-legend__item">
                <span className="mood-pie-legend__swatch" style={{ background: ANGER_TYPE_COLORS[key] }} />
                <span className="mood-pie-legend__label">{ANGER_TYPE_LABELS[key]}</span>
                <span className="mood-pie-legend__count">
                  {count} ({pct.toFixed(0)}%)
                </span>
              </div>
            );
          })}
        </div>
      </section>

      <section className="mental-patterns__history anger-patterns__history" aria-label="Anger reflections">
        <h2>Anger reflections</h2>
        <ul className="mental-patterns__history-list">
          {angerInsights.map((entry) => (
            <li key={entry.id} className="mental-patterns__history-item">
              <span
                className="mental-patterns__history-swatch"
                style={{ background: ANGER_TYPE_COLORS[entry.type] }}
                aria-hidden
              />
              <div>
                <p className="mental-patterns__history-meta">
                  {new Date(entry.date).toLocaleDateString(undefined, {
                    month: "short",
                    day: "numeric",
                    year: "numeric"
                  })}
                  {" · "}
                  {ANGER_TYPE_LABELS[entry.type]}
                  {entry.emotion ? ` · ${entry.emotion}` : ""}
                </p>
                {entry.preview ? (
                  <p className="mental-patterns__history-preview">{entry.preview}…</p>
                ) : null}
              </div>
            </li>
          ))}
        </ul>
      </section>
    </>
  );
};

export default MentalPatternsAnger;
